// models/Organization.js
import { db } from "./firebase";
import {
  collection,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

class OrganizationService {
  // Create a new organization
  // In models/Organization.js, modify the createOrganization method:

  async createOrganization(name, adminUid, adminEmail, adminName) {
    try {
      // Create a timestamp once to use consistently
      const timestamp = new Date().toISOString();

      // Create the organization document
      const orgRef = await addDoc(collection(db, "organizations"), {
        name,
        createdAt: serverTimestamp(),
        adminUid,
        adminEmail,
        members: [
          {
            uid: adminUid,
            name: adminName,
            email: adminEmail,
            role: "admin",
            joinedAt: timestamp, // Use string timestamp in array
          },
        ],
      });

      // Update the user document to reference this organization
      await updateDoc(doc(db, "users", adminUid), {
        organizationId: orgRef.id,
        organizationRole: "admin",
        organizationName: name,
      });

      return { id: orgRef.id, name };
    } catch (error) {
      console.error("Error creating organization:", error);
      throw error;
    }
  }

  // Get organization details
  async getOrganization(orgId) {
    try {
      const orgDoc = await getDoc(doc(db, "organizations", orgId));

      if (!orgDoc.exists()) {
        throw new Error("Organization not found");
      }

      return {
        id: orgDoc.id,
        ...orgDoc.data(),
      };
    } catch (error) {
      console.error("Error getting organization:", error);
      throw error;
    }
  }

  // Get organization by admin UID
  async getOrganizationByAdmin(adminUid) {
    try {
      const orgsRef = collection(db, "organizations");
      const q = query(orgsRef, where("adminUid", "==", adminUid));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      // Return the first org found (an admin should typically only have one organization)
      const orgDoc = querySnapshot.docs[0];
      return {
        id: orgDoc.id,
        ...orgDoc.data(),
      };
    } catch (error) {
      console.error("Error getting organization by admin:", error);
      throw error;
    }
  }

  // Get a user's organization
  async getUserOrganization(uid) {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));

      if (!userDoc.exists() || !userDoc.data().organizationId) {
        return null;
      }

      const orgId = userDoc.data().organizationId;
      return await this.getOrganization(orgId);
    } catch (error) {
      console.error("Error getting user organization:", error);
      throw error;
    }
  }

  // Add a member to an organization
  async addMember(orgId, email, role = "member") {
    try {
      // First check if the user with this email exists
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // User doesn't exist yet, create an invitation
        await this.createInvitation(orgId, email, role);
        return { status: "invited", email };
      }

      // User exists, add them to the organization
      const userDoc = querySnapshot.docs[0];
      const uid = userDoc.id;

      // Update the organization document
      // For the addMember method:
      await updateDoc(doc(db, "organizations", orgId), {
        members: arrayUnion({
          uid,
          email,
          role,
          joinedAt: new Date().toISOString(), // Use string timestamp instead
        }),
      });

      // Update the user document
      const orgDoc = await getDoc(doc(db, "organizations", orgId));
      await updateDoc(doc(db, "users", uid), {
        organizationId: orgId,
        organizationRole: role,
        organizationName: orgDoc.data().name,
      });

      return { status: "added", uid, email };
    } catch (error) {
      console.error("Error adding member to organization:", error);
      throw error;
    }
  }

  // Create an invitation for a user who doesn't have an account yet
  async createInvitation(orgId, email, role) {
    try {
      const invitationRef = collection(db, "invitations");

      // Create a unique invitation code
      const code = Math.random().toString(36).substring(2, 10);

      await addDoc(invitationRef, {
        organizationId: orgId,
        email,
        role,
        code,
        createdAt: serverTimestamp(),
        status: "pending",
      });

      // In a real application, you would send an email with the invitation link

      return { status: "invited", email, code };
    } catch (error) {
      console.error("Error creating invitation:", error);
      throw error;
    }
  }

  // Remove a member from an organization
  async removeMember(orgId, uid) {
    try {
      // Get the organization
      const orgDoc = await getDoc(doc(db, "organizations", orgId));

      if (!orgDoc.exists()) {
        throw new Error("Organization not found");
      }

      // Find the member to remove
      const members = orgDoc.data().members;
      const memberToRemove = members.find((member) => member.uid === uid);

      if (!memberToRemove) {
        throw new Error("Member not found in organization");
      }

      // Check that we're not removing the admin
      if (orgDoc.data().adminUid === uid) {
        throw new Error("Cannot remove the organization admin");
      }

      // Update the organization document
      await updateDoc(doc(db, "organizations", orgId), {
        members: arrayRemove(memberToRemove),
      });

      // Update the user document
      await updateDoc(doc(db, "users", uid), {
        organizationId: null,
        organizationRole: null,
        organizationName: null,
      });

      return { status: "removed", uid };
    } catch (error) {
      console.error("Error removing member from organization:", error);
      throw error;
    }
  }

  // Change a member's role
  async changeMemberRole(orgId, uid, newRole) {
    try {
      // Get the organization
      const orgDoc = await getDoc(doc(db, "organizations", orgId));

      if (!orgDoc.exists()) {
        throw new Error("Organization not found");
      }

      // Find the member to update
      const members = orgDoc.data().members;
      const memberIndex = members.findIndex((member) => member.uid === uid);

      if (memberIndex === -1) {
        throw new Error("Member not found in organization");
      }

      // Create an updated members array
      const updatedMembers = [...members];
      updatedMembers[memberIndex] = {
        ...updatedMembers[memberIndex],
        role: newRole,
      };

      // Update the organization document
      await updateDoc(doc(db, "organizations", orgId), {
        members: updatedMembers,
      });

      // Update the user document
      await updateDoc(doc(db, "users", uid), {
        organizationRole: newRole,
      });

      return { status: "updated", uid, role: newRole };
    } catch (error) {
      console.error("Error changing member role:", error);
      throw error;
    }
  }

  // Get all members of an organization
  async getOrganizationMembers(orgId) {
    try {
      const orgDoc = await getDoc(doc(db, "organizations", orgId));

      if (!orgDoc.exists()) {
        throw new Error("Organization not found");
      }

      return orgDoc.data().members || [];
    } catch (error) {
      console.error("Error getting organization members:", error);
      throw error;
    }
  }

  // Check if a user is an admin of any organization
  async isUserOrgAdmin(uid) {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));

      if (!userDoc.exists()) {
        return false;
      }

      const userData = userDoc.data();
      return userData.organizationRole === "admin";
    } catch (error) {
      console.error("Error checking if user is org admin:", error);
      throw error;
    }
  }

  async updateOrganization(orgId, updateData) {
    try {
      const cleanedData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined && value !== null)
      );

      await updateDoc(doc(db, "organizations", orgId), {
        ...cleanedData,
        updatedAt: serverTimestamp(),
      });

    } catch (error) {
      console.error("Error updating organization:", error);
      throw error;
    }
  }
}

export default new OrganizationService();
